import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { Dialog } from '@angular/cdk/dialog';
import { CalendarComponent } from '../components/calendar/calendar/calendar.component';
import { CalendarStatusService } from '../components/calendar/calendar/calendar-status.service';
import {
  BehaviorSubject,
  combineLatest,
  interval,
  map,
  shareReplay,
  startWith,
  type Subject,
} from 'rxjs';
import { switchMap, tap } from 'rxjs/operators';
import {
  addMonths,
  endOfWeek,
  formatISO,
  lastDayOfMonth,
  parseISO,
  startOfDay,
  startOfMonth,
  startOfWeek,
} from 'date-fns';
import { AsyncPipe } from '@angular/common';
import { SessionStateService } from '../../../services/session-state.service';
import type { FirestoreWork, IsoDate } from '@jessie/commons';
import { WorkService } from '../../../services/work.service';
import { ActivatedRoute, Router } from '@angular/router';
import { TimerService } from '../../../services/timer.service';
import { groupBy } from 'remeda';
import {
  WorkSlideOverComponent,
  type WorkSlideOverInput,
} from '../../projects/slide-overs/work-slide-over.component';
import type { CalendarTimeEntryChange } from '../components/calendar/week-calendar/week-calendar.component';

export function getCalendarWorkRange(
  selectedMonth: IsoDate,
  selectedDate: IsoDate = selectedMonth
): { from: IsoDate; to: IsoDate } {
  const selectedMonthDate = parseISO(selectedMonth);
  const selectedDateDate = parseISO(selectedDate);
  const monthFrom = addMonths(startOfMonth(selectedMonthDate), -2);
  const monthTo = endOfWeek(lastDayOfMonth(selectedMonthDate), { weekStartsOn: 1 });
  const weekFrom = startOfWeek(selectedDateDate, { weekStartsOn: 1 });
  const weekTo = endOfWeek(selectedDateDate, { weekStartsOn: 1 });

  return {
    from: formatISO(monthFrom < weekFrom ? monthFrom : weekFrom, { representation: 'date' }),
    to: formatISO(monthTo > weekTo ? monthTo : weekTo, { representation: 'date' }),
  };
}

@Component({
  selector: 'app-calendar-page',
  imports: [CalendarComponent, AsyncPipe],
  templateUrl: './calendar.page.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CalendarPage {
  sessionState = inject(SessionStateService);
  workService = inject(WorkService);
  activatedRoute = inject(ActivatedRoute);
  router = inject(Router);
  timerService = inject(TimerService);
  dialog = inject(Dialog);
  calendarStatus = inject(CalendarStatusService);

  selectedMonth: Subject<IsoDate> = new BehaviorSubject(
    formatISO(new Date(), { representation: 'date' })
  );

  selectedDate = this.activatedRoute.params.pipe(
    map((params) => params.selectedDate),
    tap((selectedDate) => this.selectedMonth.next(selectedDate)),
    shareReplay({ bufferSize: 1, refCount: true })
  );

  private allWorks$ = combineLatest([
    this.sessionState.getOrgaUserId(),
    this.selectedMonth,
    this.selectedDate,
  ]).pipe(
    switchMap(([userId, selectedMonth, selectedDate]) => {
      const { from, to } = getCalendarWorkRange(selectedMonth, selectedDate);
      return this.workService.getAllWorkForUser(userId, from, to);
    }),
    shareReplay(1)
  );

  workItems = this.allWorks$;

  timerItems = combineLatest([
    this.timerService.getTimers(),
    this.selectedMonth,
    this.selectedDate,
    interval(60_000).pipe(startWith(0)),
  ]).pipe(
    map(([timers, selectedMonth, selectedDate]) => {
      const { from, to } = getCalendarWorkRange(selectedMonth, selectedDate);
      return timers.filter((timer) => timer.data.date >= from && timer.data.date <= to);
    }),
    shareReplay({ bufferSize: 1, refCount: true })
  );

  work = this.allWorks$.pipe(
    map((works) => groupBy(works, (work) => startOfDay(new Date(work.data.date)).toDateString())),
    map((workMap) => {
      const w = new Map<string, number>();
      for (const [key, value] of Object.entries(workMap)) {
        w.set(
          key,
          value.reduce(
            (previousValue, currentValue) => previousValue + currentValue.data.workMinutes,
            0
          ) / 60
        );
      }
      return w;
    })
  );

  warningDays = this.timerService
    .getOutdatedTimers()
    .pipe(map((timers) => timers.map((timer) => timer.data.date)));

  weekStatusMessage = this.calendarStatus.message$;
  pendingTimeEntryChange: CalendarTimeEntryChange | null = null;

  async applyTimeEntryChange(change: CalendarTimeEntryChange): Promise<void> {
    if (this.pendingTimeEntryChange) return;

    this.pendingTimeEntryChange = change;
    try {
      switch (change.kind) {
        case 'work':
          await this.workService.updateWorkSession(
            change.work,
            change.sessionEntryId,
            {},
            change.nextEntry
          );
          break;
        case 'paused-timer':
          await this.timerService.updateEmbeddedTimeEntry(
            change.timer,
            change.sessionEntryId,
            change.nextEntry
          );
          break;
        case 'active-timer-start':
          await this.timerService.updateActiveStartTime(change.timer, change.startTime);
          break;
      }

      this.pendingTimeEntryChange = null;
      this.calendarStatus.showSaved();
    } catch {
      this.pendingTimeEntryChange = null;
      this.calendarStatus.show({
        severity: 'warning',
        text: 'Die Zeitänderung konnte nicht gespeichert werden.',
      });
    }
  }

  selectedDateChange($event: IsoDate) {
    this.router.navigate(['..', $event], { relativeTo: this.activatedRoute });
  }

  showTimer(event: { timerId: string; date: IsoDate }) {
    this.router.navigate(['..', event.date, 'timer', event.timerId], {
      relativeTo: this.activatedRoute,
    });
  }

  openCreateWork(event: { date: IsoDate; workMinutes: number; startTime: string }) {
    const dialogRef = this.dialog.open<{ saved: true }, WorkSlideOverInput, WorkSlideOverComponent>(
      WorkSlideOverComponent,
      {
        data: { date: event.date, workMinutes: event.workMinutes, startTime: event.startTime },
      }
    );

    dialogRef.closed.subscribe((result) => {
      if (result && typeof result === 'object' && 'saved' in result) {
        this.calendarStatus.showSaved();
      }
    });
  }

  openEditWork(event: FirestoreWork | { work: FirestoreWork; sessionEntryId?: string }) {
    const work = 'work' in event ? event.work : event;
    const sessionEntryId = 'work' in event ? event.sessionEntryId : undefined;
    const dialogRef = this.dialog.open<{ saved: true }, WorkSlideOverInput, WorkSlideOverComponent>(
      WorkSlideOverComponent,
      {
        data: { work, sessionEntryId },
      }
    );

    dialogRef.closed.subscribe((result) => {
      if (result && typeof result === 'object' && 'saved' in result) {
        this.calendarStatus.showSaved();
      }
    });
  }
}
